import React, { useEffect, useMemo, useState, useRef, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Circle, MessageSquare, Settings, Phone, Bot, Loader2, Save, Zap, Database, ShoppingBag, Mail, Calendar, FileText, Lock, Users, Check, AlertTriangle, Clock, LayoutDashboard, Package, Tag, MessagesSquare, Search, Plus, Download, Upload, Filter, MoreVertical, Menu, X, ChevronRight, ChevronLeft, Trash2, Edit, Power, LogOut, User, ChevronDown, Globe, Bell, Camera, Key, CreditCard, Crown, Shield, UserCheck, StickyNote, TrendingUp, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/Button';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import { useToast } from '../components/ui/Toast';
import { evolutionAPI, googleSheets, PlanType, isTrialExpired } from '../utils/api';
import { dbProducts, dbOffers, dbCustomers, dbUserNotifications } from '../utils/supabase';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ChatPage } from '../components/dashboard/ChatPage';
import { ChatWidget } from '../components/ui/ChatWidget';
import { GuidedTour, TourStep } from '../components/dashboard/GuidedTour';
import { SupportTickets } from '../components/dashboard/SupportTickets';
// --- INTERFACES ---
interface Product {
  id: string;
  name: string;
  type: 'product' | 'service';
  price: string;
  description: string;
  link?: string;
  createdAt: string;
}
interface Offer {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  discount: string;
  active: boolean;
  link?: string;
  createdAt: string;
}
interface QAItem {
  id: string;
  question: string;
  answer: string;
  details?: string;
  createdAt: string;
}
type LeadStatus = 'new_lead' | 'contacted' | 'waiting_response' | 'qualified' | 'appointment_scheduled' | 'won' | 'cancelled';
interface Customer {
  id: string;
  instanceName: string;
  name: string;
  phone: string;
  notes: string;
  status: LeadStatus;
  createdAt: string;
}
interface Conversation {
  id: string;
  customerName: string;
  phone: string;
  lastMessage: string;
  timestamp: string;
  unread: number;
}
interface AppNotification {
  id: string;
  title: string;
  titleAr: string;
  message: string;
  messageAr: string;
  type: 'info' | 'warning' | 'success';
  read: boolean;
  createdAt: string;
  isFromDb?: boolean;
}
// --- CONSTANTS ---
const PLAN_INTEGRATIONS: Record<string, string[]> = {
  starter: ['shopify'],
  business: ['shopify'],
  pro: ['shopify']
};
const INTEGRATIONS = [{
  id: 'shopify',
  name: 'Shopify',
  nameAr: 'شوبيفاي',
  description: 'Connect your Shopify store with WhatsApp bot',
  descriptionAr: 'اربط متجرك على شوبيفاي مع بوت واتساب',
  icon: ShoppingBag,
  color: 'from-green-500 to-emerald-600',
  isBeta: true
}];
type Section = 'dashboard' | 'customers' | 'products' | 'offers' | 'conversations' | 'integration' | 'settings' | 'support';
// ── Map Supabase condition → LeadStatus ──────────────────────────────────────
function mapConditionToStatus(condition?: string | null): LeadStatus {
  const map: Record<string, LeadStatus> = {
    new: 'new_lead',
    new_lead: 'new_lead',
    contacted: 'contacted',
    waiting_response: 'waiting_response',
    qualified: 'qualified',
    appointment_scheduled: 'appointment_scheduled',
    appointment: 'appointment_scheduled',
    won: 'won',
    closed: 'won',
    cancelled: 'cancelled',
    canceled: 'cancelled',
    lost: 'cancelled'
  };
  return map[condition?.toLowerCase() || ''] || 'new_lead';
}
// ─────────────────────────────────────────────────────────────────────────────
export function DashboardPage() {
  const {
    user,
    updateUser,
    logout
  } = useAuth();
  const {
    language,
    setLanguage,
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const navigate = useNavigate();
  const isRTL = language === 'ar';
  const fileInputRef = useRef<HTMLInputElement>(null);
  const customerImportRef = useRef<HTMLInputElement>(null);
  const productImportRef = useRef<HTMLInputElement>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  // --- CORE STATE ---
  const [instanceStats, setInstanceStats] = useState<any>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [systemMessage, setSystemMessage] = useState('');
  const [isSavingMessage, setIsSavingMessage] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [trialDaysLeft, setTrialDaysLeft] = useState<number | null>(null);
  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isBotEnabled, setIsBotEnabled] = useState(false);
  const [isTogglingBot, setIsTogglingBot] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  // Tour State
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);
  // Tour Steps & Handlers
  const tourSteps: TourStep[] = [{
    targetId: 'sidebar-dashboard',
    title: 'Dashboard Overview',
    titleAr: 'نظرة عامة على لوحة التحكم',
    description: 'View your key metrics, conversations, and bot progress here.',
    descriptionAr: 'اطلع على مقاييسك الرئيسية والمحادثات وتقدم البوت هنا.',
    position: 'right'
  }, {
    targetId: 'sidebar-integration',
    title: 'Connect WhatsApp',
    titleAr: 'ربط واتساب',
    description: 'Connect your WhatsApp number and set up the AI assistant.',
    descriptionAr: 'قم بربط رقم واتساب الخاص بك وإعداد المساعد الذكي.',
    position: 'right'
  }, {
    targetId: 'sidebar-products',
    title: 'Add Products & Services',
    titleAr: 'أضف المنتجات والخدمات',
    description: 'Add your products and services so the bot can inform customers.',
    descriptionAr: 'أضف منتجاتك وخدماتك ليتمكن البوت من إبلاغ العملاء.',
    position: 'right'
  }, {
    targetId: 'sidebar-customers',
    title: 'Manage Leads',
    titleAr: 'إدارة المتابعات',
    description: 'Track and manage your customer leads and follow-ups.',
    descriptionAr: 'تتبع وإدارة متابعات العملاء.',
    position: 'right'
  }, {
    targetId: 'sidebar-conversations',
    title: 'View Conversations',
    titleAr: 'عرض المحادثات',
    description: 'See all WhatsApp conversations handled by the bot.',
    descriptionAr: 'اطلع على جميع محادثات واتساب التي يتعامل معها البوت.',
    position: 'right'
  }];
  const handleTourComplete = () => {
    setIsTourOpen(false);
    setTourStepIndex(0);
    localStorage.setItem('operines_tour_seen', 'true');
  };
  const handleTourStepChange = (index: number) => {
    setTourStepIndex(index);
    // Navigate to the relevant section when tour step changes
    const stepSectionMap: Record<string, Section> = {
      'sidebar-dashboard': 'dashboard',
      'sidebar-integration': 'integration',
      'sidebar-products': 'products',
      'sidebar-customers': 'customers',
      'sidebar-conversations': 'conversations'
    };
    const targetSection = stepSectionMap[tourSteps[index]?.targetId];
    if (targetSection) {
      setActiveSection(targetSection);
    }
  };
  const [isLangOpen, setIsLangOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  // Data State
  const [products, setProducts] = useState<Product[]>([]);
  const [offers, setOffers] = useState<Offer[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isRefreshingCustomers, setIsRefreshingCustomers] = useState(false);
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<'product' | 'offer' | 'customer' | null>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  // Profile & Notifications State
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [oldPassword, setOldPassword] = useState('');
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  // Search State
  const [customerSearch, setCustomerSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<LeadStatus | 'all'>('all');
  // Extended Profile State
  const [editPhone, setEditPhone] = useState('');
  const [editBrandName, setEditBrandName] = useState('');
  const [editCompanyAddress, setEditCompanyAddress] = useState('');
  const hasConnectedWhatsApp = !!user?.instanceName;
  const hasSystemMessage = !!user?.systemMessage;
  const userPlan: PlanType = user?.plan || 'trial';
  const availableIntegrations = PLAN_INTEGRATIONS[userPlan] || [];
  const brandName = (user as any)?.brandName || user?.instanceName || user?.name || 'My Brand';
  const unreadCount = notifications.filter(n => !n.read).length;
  const supportUnreadCount = notifications.filter(n => !n.read && (n.titleAr === 'رد جديد على تذكرتك' || n.title === 'New reply on your ticket')).length;
  // --- Click outside handler ---
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setIsLangOpen(false);
        setIsProfileOpen(false);
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    // WhatsApp not connected reminder every 5 minutes
    let whatsappInterval: ReturnType<typeof setInterval> | null = null;
    if (!hasConnectedWhatsApp) {
      // Add initial notification
      const stored = JSON.parse(localStorage.getItem('operines_notifications') || '[]');
      const hasInitialReminder = stored.some((n: AppNotification) => n.id.includes('_wa_connect'));
      if (!hasInitialReminder) {
        const waNotif: AppNotification = {
          id: Date.now().toString() + '_wa_connect',
          title: 'Connect WhatsApp',
          titleAr: 'ربط واتساب',
          message: 'Please connect your WhatsApp number to start using the bot.',
          messageAr: 'يرجى ربط رقم واتساب الخاص بك لبدء استخدام البوت.',
          type: 'warning',
          read: false,
          createdAt: new Date().toISOString()
        };
        const updated = [waNotif, ...stored];
        setNotifications(updated);
        localStorage.setItem('operines_notifications', JSON.stringify(updated));
      }
      whatsappInterval = setInterval(() => {
        setNotifications(prev => {
          const waNotif: AppNotification = {
            id: Date.now().toString() + '_wa_remind',
            title: 'WhatsApp Not Connected',
            titleAr: 'واتساب غير مربوط',
            message: 'Connect your WhatsApp to activate the AI assistant.',
            messageAr: 'قم بربط واتساب لتفعيل المساعد الذكي.',
            type: 'warning',
            read: false,
            createdAt: new Date().toISOString()
          };
          const updated = [waNotif, ...prev];
          localStorage.setItem('operines_notifications', JSON.stringify(updated));
          return updated;
        });
      }, 5 * 60 * 1000); // 5 minutes
    }
    return () => {
      document.removeEventListener('mousedown', handler);
      if (whatsappInterval) clearInterval(whatsappInterval);
    };
  }, [hasConnectedWhatsApp]);
  // --- LOAD DATA ---
  useEffect(() => {
    // Load from localStorage first (instant)
    setProducts(JSON.parse(localStorage.getItem('operines_products') || '[]'));
    setOffers(JSON.parse(localStorage.getItem('operines_offers') || '[]'));
    setCustomers(JSON.parse(localStorage.getItem('operines_customers') || '[]'));
    setConversations(JSON.parse(localStorage.getItem('operines_conversations') || '[]'));
    setIsBotEnabled(localStorage.getItem('operines_bot_enabled') === 'true');
    setProfileImage(localStorage.getItem('operines_profile_image'));
    setEditName(user?.name || '');
    setEditEmail(user?.email || '');
    setEditPhone(user?.phone || '');
    setEditBrandName((user as any)?.brandName || '');
    setEditCompanyAddress((user as any)?.companyAddress || '');
    // Then fetch from Supabase (sync)
    if (user?.email) {
      const email = user.email;
      dbProducts.getAll(email).then(data => {
        if (data.length > 0) {
          const mapped = data.map(p => ({
            id: p.id || '',
            name: p.name,
            type: p.type as 'product' | 'service',
            price: p.price,
            description: p.description || '',
            link: p.link || '',
            createdAt: p.created_at || new Date().toISOString()
          }));
          setProducts(mapped);
          localStorage.setItem('operines_products', JSON.stringify(mapped));
        }
      }).catch(() => {});
      dbOffers.getAll(email).then(data => {
        if (data.length > 0) {
          const mapped = data.map(o => ({
            id: o.id || '',
            title: o.title,
            startDate: o.start_date || '',
            endDate: o.end_date || '',
            discount: o.discount,
            active: o.active ?? true,
            link: o.link || '',
            createdAt: o.created_at || new Date().toISOString()
          }));
          setOffers(mapped);
          localStorage.setItem('operines_offers', JSON.stringify(mapped));
        }
      }).catch(() => {});
      // Load customers by instanceName (the ONLY identifier in the table)
      if (user.instanceName) {
        dbCustomers.getAll(email, user.instanceName).then(data => {
          const mapped = data.map(c => ({
            id: c.phone || c.created_at || Math.random().toString(),
            instanceName: c.instance_name,
            name: c.name,
            phone: c.phone || '',
            notes: c.note || '',
            status: mapConditionToStatus(c.condition),
            createdAt: c.created_at || new Date().toISOString()
          }));
          setCustomers(mapped);
          localStorage.setItem('operines_customers', JSON.stringify(mapped));
        }).catch(() => {});
      }
    }
    // Load notifications
    const stored = JSON.parse(localStorage.getItem('operines_notifications') || '[]');
    // Fetch notifications from Supabase
    if (user?.email) {
      dbUserNotifications.getByUser(user.email).then(dbNotifs => {
        if (dbNotifs.length > 0) {
          // Map Supabase notifications to AppNotification format
          const mappedDbNotifs = dbNotifs.map(n => ({
            id: n.id || '',
            title: n.title,
            titleAr: n.title_ar,
            message: n.message,
            messageAr: n.message_ar,
            type: n.type as 'info' | 'warning' | 'success',
            read: n.read,
            createdAt: n.created_at || new Date().toISOString(),
            isFromDb: true // Flag to know which ones to update in DB
          }));
          // Merge with local notifications, avoiding duplicates by ID
          const localOnly = stored.filter((n: any) => !n.isFromDb);
          const merged = [...mappedDbNotifs, ...localOnly].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          setNotifications(merged);
          localStorage.setItem('operines_notifications', JSON.stringify(merged));
        } else if (stored.length === 0) {
          // Fallback to welcome message if no notifications at all
          const welcome: AppNotification = {
            id: Date.now().toString(),
            title: 'Welcome to Operines!',
            titleAr: 'مرحباً بك في Operines!',
            message: `You are on the ${user?.plan || 'trial'} plan.`,
            messageAr: `أنت على خطة ${user?.plan === 'trial' ? 'التجربة المجانية' : user?.plan || 'التجربة'}.`,
            type: 'info',
            read: false,
            createdAt: new Date().toISOString()
          };
          setNotifications([welcome]);
          localStorage.setItem('operines_notifications', JSON.stringify([welcome]));
        } else {
          setNotifications(stored);
        }
      }).catch(() => {
        if (stored.length === 0) {
          const welcome: AppNotification = {
            id: Date.now().toString(),
            title: 'Welcome to Operines!',
            titleAr: 'مرحباً بك في Operines!',
            message: `You are on the ${user?.plan || 'trial'} plan.`,
            messageAr: `أنت على خطة ${user?.plan === 'trial' ? 'التجربة المجانية' : user?.plan || 'التجربة'}.`,
            type: 'info',
            read: false,
            createdAt: new Date().toISOString()
          };
          setNotifications([welcome]);
          localStorage.setItem('operines_notifications', JSON.stringify([welcome]));
        } else {
          setNotifications(stored);
        }
      });
    } else {
      if (stored.length === 0) {
        const welcome: AppNotification = {
          id: Date.now().toString(),
          title: 'Welcome to Operines!',
          titleAr: 'مرحباً بك في Operines!',
          message: `You are on the ${user?.plan || 'trial'} plan.`,
          messageAr: `أنت على خطة ${user?.plan === 'trial' ? 'التجربة المجانية' : user?.plan || 'التجربة'}.`,
          type: 'info',
          read: false,
          createdAt: new Date().toISOString()
        };
        setNotifications([welcome]);
        localStorage.setItem('operines_notifications', JSON.stringify([welcome]));
      } else {
        setNotifications(stored);
      }
    }
    // Trial logic
    if (user?.plan === 'trial' && user?.trialEndDate) {
      const now = new Date();
      const endDate = new Date(user.trialEndDate);
      const diffTime = endDate.getTime() - now.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      setTrialDaysLeft(diffDays);
      if (diffDays <= 0) {
        const expired = isTrialExpired(user.trialEndDate);
        if (expired) {
          const sendSuspension = async () => {
            try {
              const url = 'https://n8n.operines.me/webhook/endacount';
              const body = JSON.stringify({
                email: user.email,
                name: user.name,
                phone: user.phone,
                instanceName: user.instanceName,
                trialEndDate: user.trialEndDate,
                suspendedAt: new Date().toISOString()
              });
              await fetch(url, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body
              }).catch(() => fetch(url, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                  'Content-Type': 'application/json'
                },
                body
              }));
              // Also notify the disable webhook on trial expiry
              const disableUrl = 'https://n8n.operines.me/webhook/disable';
              const disableBody = JSON.stringify({
                email: user.email,
                name: user.name,
                phone: user.phone,
                instanceName: user.instanceName,
                plan: user.plan,
                trialEndDate: user.trialEndDate,
                reason: 'trial_expired',
                disabledAt: new Date().toISOString()
              });
              await fetch(disableUrl, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: disableBody
              }).catch(() => fetch(disableUrl, {
                method: 'POST',
                mode: 'no-cors',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: disableBody
              }));
            } catch (e) {
              console.error(e);
            }
          };
          sendSuspension();
          updateUser({
            isAccountSuspended: true,
            isTrialActive: false
          });
        }
      }
      const today = new Date().toDateString();
      const existingReminder = stored.find((n: AppNotification) => n.type === 'warning' && new Date(n.createdAt).toDateString() === today);
      if (!existingReminder && diffDays > 0 && diffDays <= 5) {
        const reminder: AppNotification = {
          id: Date.now().toString() + '_trial',
          title: `${diffDays} days left in trial`,
          titleAr: `متبقي ${diffDays} أيام من التجربة`,
          message: 'Upgrade now to keep your bot running.',
          messageAr: 'قم بالترقية الآن للحفاظ على عمل البوت.',
          type: diffDays <= 2 ? 'warning' : 'info',
          read: false,
          createdAt: new Date().toISOString()
        };
        const updated = [reminder, ...stored];
        setNotifications(updated);
        localStorage.setItem('operines_notifications', JSON.stringify(updated));
      }
    }
    // Paid subscription expiry check
    const subscriptionEndDate = (user as any)?.subscriptionEndDate;
    if (user?.plan && user.plan !== 'trial' && subscriptionEndDate) {
      const now = new Date();
      const subEnd = new Date(subscriptionEndDate);
      if (now > subEnd) {
        const disableUrl = 'https://n8n.operines.me/webhook/disable';
        const disableBody = JSON.stringify({
          email: user.email,
          name: user.name,
          phone: user.phone,
          instanceName: user.instanceName,
          plan: user.plan,
          subscriptionEndDate,
          reason: 'subscription_expired',
          disabledAt: new Date().toISOString()
        });
        fetch(disableUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: disableBody
        }).catch(() => fetch(disableUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: disableBody
        }));
        updateUser({
          isAccountSuspended: true
        });
      }
    }
    if (hasConnectedWhatsApp && user?.instanceName) loadInstanceStats();
    if (user?.systemMessage) setSystemMessage(user.systemMessage);
    // Check if tour should run (new user or not seen)
    const tourSeen = localStorage.getItem('operines_tour_seen');
    if (!tourSeen) {
      // Small delay to ensure loading is done
      setTimeout(() => setIsTourOpen(true), 1500);
    }
  }, [hasConnectedWhatsApp, user?.instanceName, user?.systemMessage, user?.trialEndDate]);
  // ── Refresh customers from webhook ──────────────────────────────────────────
  const refreshCustomers = async () => {
    if (!user?.instanceName || isRefreshingCustomers) return;
    setIsRefreshingCustomers(true);
    try {
      const response = await fetch('https://n8n.operines.me/webhook/customer/view', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          instanceName: user.instanceName,
          email: user.email || ''
        })
      });
      if (response.ok) {
        const data = await response.json();
        // Support both array response and { customers: [...] }
        const raw: any[] = Array.isArray(data) ? data : Array.isArray(data?.customers) ? data.customers : [];
        // ✅ Filter ONLY records matching this user's instanceName
        const filtered = raw.filter((c: any) => !c.instanceName || c.instanceName?.toLowerCase() === user.instanceName?.toLowerCase());
        if (filtered.length > 0) {
          const mapped: Customer[] = filtered.map((c: any) => ({
            // Response uses `id` (numeric) as identifier
            id: String(c.id || c.phone || c.Phone || Math.random()),
            instanceName: user.instanceName || '',
            // Response uses `customerName` not `name`
            name: c.customerName || c.name || '',
            // Response uses `Phone` (capital P)
            phone: String(c.Phone || c.phone || ''),
            notes: c.notes || c.note || '',
            // No condition field in response → keep existing or default
            status: mapConditionToStatus(c.condition || c.status),
            createdAt: c.createdAt || c.created_at || new Date().toISOString()
          }));
          setCustomers(mapped);
          localStorage.setItem('operines_customers', JSON.stringify(mapped));
          showToast('success', isRTL ? 'تم تحديث العملاء' : 'Customers updated');
        } else {
          showToast('info' as any, isRTL ? 'لا يوجد عملاء لهذا الحساب' : 'No customers for this account');
        }
      }
    } catch (err) {
      showToast('error', isRTL ? 'فشل التحديث' : 'Refresh failed');
    } finally {
      setIsRefreshingCustomers(false);
    }
  };
  // ─────────────────────────────────────────────────────────────────────────────
  // --- HANDLERS (ALL EXISTING PRESERVED) ---
  const loadInstanceStats = async () => {
    if (!user?.instanceName) return;
    setIsLoadingStats(true);
    try {
      const stats = await evolutionAPI.getInstanceStats(user.instanceName);
      setInstanceStats(stats);
    } catch (e) {
      console.error(e);
      setInstanceStats({
        state: 'close'
      });
    } finally {
      setIsLoadingStats(false);
    }
  };
  const handleSaveSystemMessage = async () => {
    if (!user?.instanceName || !systemMessage.trim()) {
      showToast('error', isRTL ? 'يرجى إدخال رسالة النظام' : 'Please enter a system message');
      return;
    }
    setIsSavingMessage(true);
    try {
      // Include websiteUrl in the saved config
      await googleSheets.saveConfig({
        ...user,
        systemMessage: `${systemMessage}\n\nWebsite: ${websiteUrl}`,
        instanceName: user.instanceName
      });
      // If PDF is selected, we would upload it here
      // For now, we just simulate it or handle it if backend supported
      if (pdfFile) {
        console.log('Uploading PDF:', pdfFile.name);
        // Upload logic would go here
      }
      updateUser({
        systemMessage
      });
      showToast('success', isRTL ? 'تم حفظ الإعدادات بنجاح' : 'Settings saved successfully');
    } catch (e: any) {
      showToast('error', e.message || (isRTL ? 'فشل في الحفظ' : 'Failed to save'));
    } finally {
      setIsSavingMessage(false);
    }
  };
  const handleFinish = async () => {
    if (!user?.instanceName) {
      showToast('error', isRTL ? 'لا يوجد اتصال واتساب' : 'No WhatsApp connection');
      return;
    }
    setIsFinishing(true);
    try {
      const webhookUrl = 'https://n8n.operines.me/webhook/seup-whatsapp-operines1';
      const payload = {
        instanceName: user.instanceName,
        evoServerUrl: 'https://evo.operines.me',
        evoApiKey: '68MlLUENzjjV142ye8r8NaLHONSvIABG',
        webhookUrl,
        enableBot: true,
        timestamp: new Date().toISOString()
      };
      const response = await fetch(webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (response.ok || response.status === 200) {
        setInstanceStats({
          ...instanceStats,
          state: 'open'
        });
        showToast('success', isRTL ? 'تم تفعيل المساعد الذكي بنجاح!' : 'AI Assistant activated successfully!');
        setTimeout(() => loadInstanceStats(), 2000);
      } else throw new Error(`HTTP ${response.status}`);
    } catch (e: any) {
      if (e.message.includes('CORS') || e.message.includes('Failed to fetch')) {
        try {
          await fetch('https://n8n.operines.me/webhook/seup-whatsapp-operines1', {
            method: 'POST',
            mode: 'no-cors',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              instanceName: user.instanceName,
              evoServerUrl: 'https://evo.operines.me',
              evoApiKey: '68MlLUENzjjV142ye8r8NaLHONSvIABG',
              webhookUrl: 'https://n8n.operines.me/webhook/seup-whatsapp-operines1',
              enableBot: true,
              timestamp: new Date().toISOString()
            })
          });
          setInstanceStats({
            ...instanceStats,
            state: 'open'
          });
          showToast('success', isRTL ? 'تم تفعيل المساعد الذكي بنجاح!' : 'AI Assistant activated successfully!');
          setTimeout(() => loadInstanceStats(), 2000);
        } catch {
          showToast('error', isRTL ? 'حدث خطأ في التفعيل' : 'Activation error occurred');
        }
      } else showToast('error', isRTL ? 'حدث خطأ في التفعيل' : 'Activation error occurred');
    } finally {
      setIsFinishing(false);
    }
  };
  // --- Helper: Send disable webhook ---
  const sendDisableWebhook = async (reason: string) => {
    const DISABLE_WEBHOOK = 'https://n8n.operines.me/webhook/disable';
    const payload = {
      email: user?.email,
      name: user?.name,
      phone: user?.phone,
      instanceName: user?.instanceName,
      plan: user?.plan,
      trialEndDate: user?.trialEndDate,
      reason,
      disabledAt: new Date().toISOString()
    };
    try {
      await fetch(DISABLE_WEBHOOK, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }).catch(() => fetch(DISABLE_WEBHOOK, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      }));
    } catch (e) {
      console.error('Failed to send disable webhook:', e);
    }
  };
  const toggleBotState = async (enable: boolean) => {
    if (!user?.instanceName) return;
    // If enabling, check requirements
    if (enable) {
      if (!hasConnectedWhatsApp) {
        showToast('error', isRTL ? 'يرجى ربط واتساب أولاً' : 'Please connect WhatsApp first');
        return;
      }
      if (!hasSystemMessage) {
        showToast('error', isRTL ? 'يرجى إعداد رسالة النظام' : 'Please set up system message');
        return;
      }
    }
    setIsTogglingBot(true);
    try {
      if (enable) {
        // ENABLE: Send to setup webhook ONLY
        const enableUrl = 'https://n8n.operines.me/webhook/seup-whatsapp-operines1';
        const enablePayload = {
          email: user.email,
          name: user.name,
          phone: user.phone,
          instanceName: user.instanceName,
          evoServerUrl: 'https://evo.operines.me',
          evoApiKey: '68MlLUENzjjV142ye8r8NaLHONSvIABG',
          webhookUrl: enableUrl,
          enableBot: true,
          plan: user.plan,
          subscriptionRemainingDays: trialDaysLeft || 0,
          customerId: user.email,
          timestamp: new Date().toISOString()
        };
        await fetch(enableUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(enablePayload)
        }).catch(() => fetch(enableUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(enablePayload)
        }));
      } else {
        // DISABLE: Send to disable webhook ONLY
        const disableUrl = 'https://n8n.operines.me/webhook/disable';
        const disablePayload = {
          email: user.email,
          name: user.name,
          phone: user.phone,
          instanceName: user.instanceName,
          plan: user.plan,
          trialEndDate: user.trialEndDate,
          reason: 'manual_disable',
          disabledAt: new Date().toISOString()
        };
        await fetch(disableUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(disablePayload)
        }).catch(() => fetch(disableUrl, {
          method: 'POST',
          mode: 'no-cors',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(disablePayload)
        }));
      }
      setIsBotEnabled(enable);
      localStorage.setItem('operines_bot_enabled', String(enable));
      showToast('success', isRTL ? enable ? 'تم تفعيل البوت' : 'تم تعطيل البوت' : enable ? 'Bot Enabled' : 'Bot Disabled');
    } catch {
      showToast('error', isRTL ? 'حدث خطأ' : 'An error occurred');
    } finally {
      setIsTogglingBot(false);
    }
  };
  // --- CRUD ---
  const saveItem = (type: string, item: any) => {
    let updated: any[];
    const isNew = !editingItem;
    const userEmail = user?.email || '';
    switch (type) {
      case 'product':
        updated = editingItem ? products.map(p => p.id === item.id ? item : p) : [...products, {
          ...item,
          id: Date.now().toString(),
          createdAt: new Date().toISOString()
        }];
        setProducts(updated);
        localStorage.setItem('operines_products', JSON.stringify(updated));
        if (isNew) {
          dbProducts.create({
            user_email: userEmail,
            name: item.name,
            type: item.type || 'product',
            price: item.price || '',
            description: item.description || '',
            link: item.link || ''
          }).catch(() => {});
          const productPayload = {
            ...item,
            id: Date.now().toString(),
            instanceName: user?.instanceName || '',
            ownerName: user?.name || '',
            ownerEmail: userEmail,
            ownerPhone: user?.phone || '',
            timestamp: new Date().toISOString()
          };
          fetch('https://n8n.operines.me/webhook/operines.ai-products', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(productPayload)
          }).catch(() => {
            fetch('https://n8n.operines.me/webhook/operines.ai-products', {
              method: 'POST',
              mode: 'no-cors',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(productPayload)
            }).catch(() => {});
          });
        } else {
          dbProducts.update(item.id, {
            name: item.name,
            type: item.type,
            price: item.price,
            description: item.description,
            link: item.link
          }).catch(() => {});
        }
        break;
      case 'offer':
        updated = editingItem ? offers.map(o => o.id === item.id ? item : o) : [...offers, {
          ...item,
          id: Date.now().toString(),
          createdAt: new Date().toISOString()
        }];
        setOffers(updated);
        localStorage.setItem('operines_offers', JSON.stringify(updated));
        if (isNew) {
          dbOffers.create({
            user_email: userEmail,
            title: item.title,
            discount: item.discount || '',
            start_date: item.startDate || '',
            end_date: item.endDate || '',
            link: item.link || '',
            active: true
          }).catch(() => {});
          const offerPayload = {
            ...item,
            id: Date.now().toString(),
            instanceName: user?.instanceName || '',
            ownerName: user?.name || '',
            ownerEmail: userEmail,
            ownerPhone: user?.phone || '',
            timestamp: new Date().toISOString()
          };
          fetch('https://n8n.operines.me/webhook/operines.ai-offers', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(offerPayload)
          }).catch(() => {
            fetch('https://n8n.operines.me/webhook/operines.ai-offers', {
              method: 'POST',
              mode: 'no-cors',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(offerPayload)
            }).catch(() => {});
          });
        } else {
          dbOffers.update(item.id, {
            title: item.title,
            discount: item.discount,
            start_date: item.startDate,
            end_date: item.endDate,
            link: item.link
          }).catch(() => {});
        }
        break;
      case 'customer':
        const customerData = {
          ...item,
          status: item.status || 'new_lead',
          notes: item.notes || ''
        };
        updated = editingItem ? customers.map(c => c.id === item.id ? {
          ...c,
          ...customerData
        } : c) : [...customers, {
          ...customerData,
          id: item.phone || Date.now().toString(),
          instanceName: user?.instanceName || '',
          createdAt: new Date().toISOString()
        }];
        setCustomers(updated);
        localStorage.setItem('operines_customers', JSON.stringify(updated));
        if (isNew) {
          dbCustomers.create({
            instance_name: user?.instanceName || '',
            name: item.name,
            phone: item.phone || '',
            note: item.notes || '',
            condition: item.status || 'new_lead'
          }).catch(() => {});
        } else {
          // Update by instance_name + phone (no id column)
          dbCustomers.update(user?.instanceName || '', editingItem.phone, {
            name: item.name,
            phone: item.phone,
            note: item.notes,
            condition: item.status
          }).catch(() => {});
        }
        break;
    }
    setIsModalOpen(false);
    setEditingItem(null);
    showToast('success', isRTL ? 'تم الحفظ بنجاح' : 'Saved successfully');
  };
  const deleteItem = (type: string, id: string) => {
    if (!confirm(isRTL ? 'هل أنت متأكد من الحذف؟' : 'Are you sure?')) return;
    let updated: any[];
    switch (type) {
      case 'product':
        updated = products.filter(p => p.id !== id);
        setProducts(updated);
        localStorage.setItem('operines_products', JSON.stringify(updated));
        dbProducts.delete(id).catch(() => {});
        break;
      case 'offer':
        updated = offers.filter(o => o.id !== id);
        setOffers(updated);
        localStorage.setItem('operines_offers', JSON.stringify(updated));
        dbOffers.delete(id).catch(() => {});
        break;
      case 'customer':
        const customerToDelete = customers.find(c => c.id === id);
        updated = customers.filter(c => c.id !== id);
        setCustomers(updated);
        localStorage.setItem('operines_customers', JSON.stringify(updated));
        if (customerToDelete) {
          dbCustomers.delete(user?.instanceName || '', customerToDelete.phone).catch(() => {});
        }
        break;
    }
    showToast('success', isRTL ? 'تم الحذف' : 'Deleted');
  };
  const openModal = (type: 'product' | 'offer' | 'customer', item: any = null) => {
    setModalType(type);
    setEditingItem(item);
    setIsModalOpen(true);
  };
  // --- PROFILE ---
  const handleProfileImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result as string;
      setProfileImage(base64);
      localStorage.setItem('operines_profile_image', base64);
      showToast('success', isRTL ? 'تم تحديث الصورة' : 'Image updated');
    };
    reader.readAsDataURL(file);
  };
  const handleSaveProfile = () => {
    if (!editName.trim()) {
      showToast('error', isRTL ? 'الاسم مطلوب' : 'Name is required');
      return;
    }
    setIsSavingProfile(true);
    updateUser({
      name: editName,
      email: editEmail,
      phone: editPhone,
      brandName: editBrandName,
      companyAddress: editCompanyAddress
    } as any);
    showToast('success', isRTL ? 'تم تحديث البيانات' : 'Profile updated');
    setIsSavingProfile(false);
  };
  const handleResetPassword = async () => {
    if (!oldPassword) {
      showToast('error', isRTL ? 'يرجى إدخال كلمة المرور القديمة' : 'Please enter old password');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      showToast('error', isRTL ? 'كلمة المرور يجب أن تكون 6 أحرف على الأقل' : 'Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('error', isRTL ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }
    // Get stored password - try Supabase first, then localStorage
    const {
      hashPassword
    } = await import('../utils/security');
    const {
      verifyPassword
    } = await import('../utils/security');
    const {
      dbUsers
    } = await import('../utils/supabase');
    let storedPasswordHash = '';
    // Try Supabase first
    try {
      const supabaseUser = await dbUsers.getByEmail(user?.email || '');
      if (supabaseUser?.password) {
        storedPasswordHash = supabaseUser.password;
      }
    } catch (e) {
      console.error('Supabase fetch error:', e);
    }
    // Fallback to localStorage
    if (!storedPasswordHash) {
      const userKey = `operiens_user_${user?.email?.toLowerCase()}`;
      const stored = localStorage.getItem(userKey);
      if (stored) {
        const data = JSON.parse(stored);
        storedPasswordHash = data.password;
      }
    }
    if (!storedPasswordHash) {
      showToast('error', isRTL ? 'لم يتم العثور على بيانات الحساب' : 'Account data not found');
      return;
    }
    // Verify old password
    const isValid = await verifyPassword(oldPassword, storedPasswordHash);
    if (!isValid) {
      showToast('error', isRTL ? 'كلمة المرور القديمة غير صحيحة' : 'Old password is incorrect');
      return;
    }
    // Hash new password
    const hashedNew = await hashPassword(newPassword);
    // Update Supabase (PRIMARY storage)
    try {
      const updated = await dbUsers.update(user?.email || '', {
        password: hashedNew
      } as any);
      if (updated) {
        console.log('✅ Password updated in Supabase');
      } else {
        console.error('❌ Failed to update password in Supabase');
      }
    } catch (e) {
      console.error('Supabase password update error:', e);
    }
    // Also update localStorage as backup
    const userKey = `operiens_user_${user?.email?.toLowerCase()}`;
    const stored = localStorage.getItem(userKey);
    if (stored) {
      const data = JSON.parse(stored);
      data.password = hashedNew;
      localStorage.setItem(userKey, JSON.stringify(data));
    }
    localStorage.setItem('operiens_user', JSON.stringify({
      ...user,
      password: hashedNew
    }));
    setOldPassword('');
    setNewPassword('');
    setConfirmPassword('');
    showToast('success', isRTL ? 'تم تغيير كلمة المرور. سيتم تسجيل خروجك الآن.' : 'Password changed. Logging you out now.');
    // Logout after 1.5 seconds
    setTimeout(() => {
      logout();
      navigate('/login');
    }, 1500);
  };
  // --- NOTIFICATIONS ---
  const markAllAsRead = () => {
    const updated = notifications.map(n => ({
      ...n,
      read: true
    }));
    setNotifications(updated);
    localStorage.setItem('operines_notifications', JSON.stringify(updated));
    // Update in Supabase
    if (user?.email) {
      dbUserNotifications.markAllAsRead(user.email).catch(console.error);
    }
  };
  // --- CHART DATA ---
  const botProgress = useMemo(() => {
    let p = 0;
    if (hasConnectedWhatsApp) p += 33;
    if (hasSystemMessage) p += 33;
    if (products.length > 0) p += 34;
    return p;
  }, [hasConnectedWhatsApp, hasSystemMessage, products.length]);
  // --- EXCEL EXPORT/IMPORT ---
  const exportToCSV = (data: any[], filename: string, headers: string[]) => {
    const csvRows = [headers.join(',')];
    data.forEach(row => {
      const values = headers.map(h => {
        const key = h.toLowerCase().replace(/ /g, '');
        const mappedKey = key === 'الاسم' || key === 'name' ? 'name' : key === 'الهاتف' || key === 'phone' ? 'phone' : key === 'البريد' || key === 'email' ? 'email' : key === 'اسمالمنتج' || key === 'productname' ? 'name' : key === 'النوع' || key === 'type' ? 'type' : key === 'السعر' || key === 'price' ? 'price' : key === 'الوصف' || key === 'description' ? 'description' : key;
        const val = row[mappedKey] || '';
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvRows.push(values.join(','));
    });
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvRows.join('\n')], {
      type: 'text/csv;charset=utf-8;'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    showToast('success', isRTL ? 'تم التصدير بنجاح' : 'Exported successfully');
  };
  const importCSV = (file: File, type: 'customer' | 'product') => {
    const reader = new FileReader();
    reader.onload = e => {
      const text = e.target?.result as string;
      if (!text) return;
      const lines = text.split('\n').filter(l => l.trim());
      if (lines.length < 2) {
        showToast('error', isRTL ? 'الملف فارغ أو غير صالح' : 'File is empty or invalid');
        return;
      }
      const headers = lines[0].split(',').map(h => h.replace(/"/g, '').trim().toLowerCase());
      const items: any[] = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].match(/(".*?"|[^",\s]+)(?=\s*,|\s*$)/g) || lines[i].split(',');
        const cleanValues = values.map(v => v.replace(/^"|"$/g, '').replace(/""/g, '"').trim());
        if (type === 'customer') {
          const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('اسم'));
          const phoneIdx = headers.findIndex(h => h.includes('phone') || h.includes('هاتف'));
          const emailIdx = headers.findIndex(h => h.includes('email') || h.includes('بريد'));
          if (cleanValues[nameIdx >= 0 ? nameIdx : 0]) {
            items.push({
              id: Date.now().toString() + '_' + i,
              name: cleanValues[nameIdx >= 0 ? nameIdx : 0] || '',
              phone: cleanValues[phoneIdx >= 0 ? phoneIdx : 1] || '',
              email: cleanValues[emailIdx >= 0 ? emailIdx : 2] || '',
              conversations: 0,
              createdAt: new Date().toISOString()
            });
          }
        } else if (type === 'product') {
          const nameIdx = headers.findIndex(h => h.includes('name') || h.includes('اسم') || h.includes('منتج'));
          const typeIdx = headers.findIndex(h => h.includes('type') || h.includes('نوع'));
          const priceIdx = headers.findIndex(h => h.includes('price') || h.includes('سعر'));
          const descIdx = headers.findIndex(h => h.includes('desc') || h.includes('وصف'));
          if (cleanValues[nameIdx >= 0 ? nameIdx : 0]) {
            items.push({
              id: Date.now().toString() + '_' + i,
              name: cleanValues[nameIdx >= 0 ? nameIdx : 0] || '',
              type: cleanValues[typeIdx >= 0 ? typeIdx : 1] || 'product',
              price: cleanValues[priceIdx >= 0 ? priceIdx : 2] || '0',
              description: cleanValues[descIdx >= 0 ? descIdx : 3] || '',
              createdAt: new Date().toISOString()
            });
          }
        }
      }
      if (items.length === 0) {
        showToast('error', isRTL ? 'لم يتم العثور على بيانات صالحة' : 'No valid data found');
        return;
      }
      if (type === 'customer') {
        const merged = [...customers, ...items];
        setCustomers(merged);
        localStorage.setItem('operines_customers', JSON.stringify(merged));
      } else {
        const merged = [...products, ...items];
        setProducts(merged);
        localStorage.setItem('operines_products', JSON.stringify(merged));
      }
      showToast('success', isRTL ? `تم استيراد ${items.length} عنصر بنجاح` : `${items.length} items imported successfully`);
    };
    reader.readAsText(file);
  };
  // --- FILTERED DATA ---
  const filteredCustomers = useMemo(() => {
    let list = customers;
    if (statusFilter !== 'all') {
      list = list.filter(c => c.status === statusFilter);
    }
    if (!customerSearch.trim()) return list;
    const q = customerSearch.toLowerCase();
    return list.filter(c => c.name.toLowerCase().includes(q) || c.phone.toLowerCase().includes(q) || (c.notes || '').toLowerCase().includes(q));
  }, [customers, customerSearch, statusFilter]);
  const filteredProducts = useMemo(() => {
    if (!productSearch.trim()) return products;
    const q = productSearch.toLowerCase();
    return products.filter(p => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.price.toLowerCase().includes(q));
  }, [products, productSearch]);
  const chartData = useMemo(() => [{
    name: 'Jan',
    conversations: 40,
    customers: 24
  }, {
    name: 'Feb',
    conversations: 30,
    customers: 13
  }, {
    name: 'Mar',
    conversations: 20,
    customers: 58
  }, {
    name: 'Apr',
    conversations: 27,
    customers: 39
  }, {
    name: 'May',
    conversations: 18,
    customers: 48
  }, {
    name: 'Jun',
    conversations: 23,
    customers: 38
  }, {
    name: 'Jul',
    conversations: 34,
    customers: 43
  }], []);
  // --- RENDER: DASHBOARD ---
  const renderDashboard = () => <div className="space-y-6" data-id="element-1510">
      {!hasConnectedWhatsApp && <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md flex justify-between items-center" data-id="element-1511">
          <div className="flex items-center gap-3" data-id="element-1512">
            <AlertTriangle className="h-5 w-5 text-yellow-400" data-id="element-1513" />
            <p className="text-sm text-yellow-700" data-id="element-1514">
              {isRTL ? 'تنبيه: لم يتم ربط واتساب بعد.' : 'WhatsApp not connected yet.'}
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setActiveSection('integration')} data-id="element-1515">
            {isRTL ? 'ربط' : 'Connect'}
          </Button>
        </div>}
      {user?.plan === 'trial' && trialDaysLeft !== null && trialDaysLeft > 0 && <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 border border-yellow-200 rounded-xl flex items-center gap-3" data-id="element-1516">
            <Clock className="w-6 h-6 text-yellow-600" data-id="element-1517" />
            <div className="flex-1" data-id="element-1518">
              <p className="font-bold text-gray-900" data-id="element-1519">
                {isRTL ? `متبقي ${trialDaysLeft} أيام من التجربة المجانية` : `${trialDaysLeft} days left in free trial`}
              </p>
            </div>
            <Button size="sm" onClick={() => navigate('/subscription')} className="bg-[#4f1964] text-white hover:bg-[#3d1350]" data-id="element-1520">
              {isRTL ? 'اشترك الآن' : 'Subscribe Now'}
            </Button>
          </motion.div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1521">
        {[{
        label: isRTL ? 'المحادثات' : 'Conversations',
        value: conversations.length,
        icon: MessagesSquare,
        color: 'text-green-600',
        bg: 'bg-green-50'
      }, {
        label: isRTL ? 'إجمالي العملاء' : 'Total Customers',
        value: customers.length,
        icon: Users,
        color: 'text-orange-600',
        bg: 'bg-orange-50'
      }, {
        label: isRTL ? 'المنتجات والخدمات' : 'Products & Services',
        value: products.length,
        icon: Package,
        color: 'text-purple-600',
        bg: 'bg-purple-50'
      }].map((stat, i) => <motion.div key={i} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: i * 0.1
      }} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-1522">
            <div className="flex justify-between items-start mb-4" data-id="element-1523">
              <div className={`p-3 rounded-lg ${stat.bg}`} data-id="element-1524">
                <stat.icon className={`w-6 h-6 ${stat.color}`} data-id="element-1525" />
              </div>
              <span className="text-2xl font-bold text-gray-900" data-id="element-1526">
                {stat.value}
              </span>
            </div>
            <h3 className="text-gray-600 font-medium" data-id="element-1527">{stat.label}</h3>
          </motion.div>)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1528">
        <div className="lg:col-span-2 bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-1529">
          <h3 className="font-bold text-lg text-gray-900 mb-6" data-id="element-1530">
            {isRTL ? 'تحليل المحادثات والعملاء' : 'Conversations & Customers'}
          </h3>
          <div className="h-64 w-full" data-id="element-1531">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1532">
              <AreaChart data={chartData} data-id="element-1533">
                <defs data-id="element-1534">
                  <linearGradient id="colorConv" x1="0" y1="0" x2="0" y2="1" data-id="element-1535">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} data-id="element-1536" />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} data-id="element-1537" />
                  </linearGradient>
                  <linearGradient id="colorCust" x1="0" y1="0" x2="0" y2="1" data-id="element-1538">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} data-id="element-1539" />
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} data-id="element-1540" />
                  </linearGradient>
                </defs>
                <XAxis dataKey="name" data-id="element-1541" />
                <YAxis data-id="element-1542" />
                <CartesianGrid strokeDasharray="3 3" data-id="element-1543" />
                <Tooltip data-id="element-1544" />
                <Area type="monotone" dataKey="conversations" stroke="#8884d8" fillOpacity={1} fill="url(#colorConv)" data-id="element-1545" />
                <Area type="monotone" dataKey="customers" stroke="#82ca9d" fillOpacity={1} fill="url(#colorCust)" data-id="element-1546" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm flex flex-col items-center justify-center" data-id="element-1547">
          <h3 className="font-bold text-lg text-gray-900 mb-6 w-full text-start" data-id="element-1548">
            {isRTL ? 'اكتمال البوت' : 'Bot Progress'}
          </h3>
          <div className="relative w-48 h-48" data-id="element-1549">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1550">
              <PieChart data-id="element-1551">
                <Pie data={[{
                value: botProgress
              }, {
                value: 100 - botProgress
              }]} cx="50%" cy="50%" innerRadius={60} outerRadius={80} startAngle={90} endAngle={-270} dataKey="value" data-id="element-1552">
                  <Cell fill="#4f1964" data-id="element-1553" />
                  <Cell fill="#e5e7eb" data-id="element-1554" />
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div className="absolute inset-0 flex items-center justify-center" data-id="element-1555">
              <span className="text-3xl font-bold text-[#4f1964]" data-id="element-1556">
                {botProgress}%
              </span>
            </div>
          </div>
          <p className="text-sm text-gray-500 mt-4 text-center" data-id="element-1557">
            {isRTL ? 'أكمل إعدادات البوت' : 'Complete bot setup'}
          </p>
        </div>
      </div>
    </div>;
  // --- RENDER: CUSTOMERS (Follow-up) ---
  const LEAD_STATUSES: {
    value: LeadStatus | 'all';
    labelAr: string;
    labelEn: string;
    color: string;
    bg: string;
    dot: string;
  }[] = [{
    value: 'all',
    labelAr: 'الكل',
    labelEn: 'All',
    color: 'text-gray-700',
    bg: 'bg-gray-100',
    dot: 'bg-gray-400'
  }, {
    value: 'new_lead',
    labelAr: 'جديد',
    labelEn: 'New Lead',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    dot: 'bg-blue-500'
  }, {
    value: 'contacted',
    labelAr: 'تم التواصل',
    labelEn: 'Contacted',
    color: 'text-indigo-700',
    bg: 'bg-indigo-100',
    dot: 'bg-indigo-500'
  }, {
    value: 'waiting_response',
    labelAr: 'في انتظار الرد',
    labelEn: 'Waiting Response',
    color: 'text-yellow-700',
    bg: 'bg-yellow-100',
    dot: 'bg-yellow-500'
  }, {
    value: 'qualified',
    labelAr: 'مؤهل',
    labelEn: 'Qualified',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    dot: 'bg-purple-500'
  }, {
    value: 'appointment_scheduled',
    labelAr: 'موعد محدد',
    labelEn: 'Appointment',
    color: 'text-orange-700',
    bg: 'bg-orange-100',
    dot: 'bg-orange-500'
  }, {
    value: 'won',
    labelAr: 'تم الفوز',
    labelEn: 'Won',
    color: 'text-green-700',
    bg: 'bg-green-100',
    dot: 'bg-green-500'
  }, {
    value: 'cancelled',
    labelAr: 'ملغي',
    labelEn: 'Cancelled',
    color: 'text-red-700',
    bg: 'bg-red-100',
    dot: 'bg-red-500'
  }];
  const getStatusInfo = (status: LeadStatus) => LEAD_STATUSES.find(s => s.value === status) || LEAD_STATUSES[1];
  const renderCustomers = () => <div className="space-y-6" data-id="element-1558">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1559">
        <div data-id="element-1560">
          <h2 className="text-xl font-bold text-gray-900" data-id="element-1561">
            {isRTL ? 'المتابعة' : 'Follow-up'}
          </h2>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-1562">
            {isRTL ? `${customers.length} متابعة إجمالاً` : `${customers.length} leads total`}
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-1563">
          {/* Refresh Button */}
          <button onClick={refreshCustomers} disabled={isRefreshingCustomers} title={isRTL ? 'تحديث العملاء' : 'Refresh customers'} className="p-2 rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-[#4f1964] hover:border-[#4f1964] transition-colors disabled:opacity-50" data-id="element-1564">
            <RefreshCw className={`w-4 h-4 ${isRefreshingCustomers ? 'animate-spin' : ''}`} data-id="element-1565" />
          </button>
          <Button className="bg-[#4f1964] text-white" size="sm" onClick={() => openModal('customer')} data-id="element-1566">
            <Plus className="w-4 h-4 mr-1" data-id="element-1567" />
            {isRTL ? 'إضافة متابعة' : 'Add Lead'}
          </Button>
        </div>
      </div>

      {/* Status Filter Tabs */}
      <div className="flex gap-2 flex-wrap" data-id="element-1568">
        {LEAD_STATUSES.map(s => {
        const count = s.value === 'all' ? customers.length : customers.filter(c => c.status === s.value).length;
        return <button key={s.value} onClick={() => setStatusFilter(s.value)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${statusFilter === s.value ? `${s.bg} ${s.color} border-transparent shadow-sm` : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'}`} data-id="element-1569">
              <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} data-id="element-1570" />
              {isRTL ? s.labelAr : s.labelEn}
              <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${statusFilter === s.value ? 'bg-white/60' : 'bg-gray-100'}`} data-id="element-1571">
                {count}
              </span>
            </button>;
      })}
      </div>

      {/* Search */}
      <div className="relative" data-id="element-1572">
        <Search className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} data-id="element-1573" />
        <input value={customerSearch} onChange={e => setCustomerSearch(e.target.value)} placeholder={isRTL ? 'بحث بالاسم أو الهاتف أو الملاحظات...' : 'Search by name, phone, or notes...'} className={`w-full py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#4f1964] focus:border-transparent text-sm ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`} data-id="element-1574" />
        {customerSearch && <button onClick={() => setCustomerSearch('')} className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${isRTL ? 'left-3' : 'right-3'}`} data-id="element-1575">
            <X className="w-4 h-4" data-id="element-1576" />
          </button>}
      </div>

      {/* Cards Grid */}
      {filteredCustomers.length === 0 ? <div className="bg-white p-12 rounded-xl border text-center" data-id="element-1577">
          <UserCheck className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1578" />
          <p className="text-gray-500 font-medium" data-id="element-1579">
            {customerSearch || statusFilter !== 'all' ? isRTL ? 'لا توجد نتائج' : 'No results found' : isRTL ? 'لا توجد متابعات بعد' : 'No leads yet'}
          </p>
          {!customerSearch && statusFilter === 'all' && <p className="text-gray-400 text-sm mt-1" data-id="element-1580">
              {isRTL ? 'أضف متابعة جديدة للبدء' : 'Add a new lead to get started'}
            </p>}
        </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1581">
          {filteredCustomers.map((c, i) => {
        const statusInfo = getStatusInfo(c.status || 'new_lead');
        // Clean phone for WhatsApp link
        const cleanPhone = c.phone ? c.phone.replace('@s.whatsapp.net', '').replace(/\D/g, '') : '';
        const waLink = cleanPhone ? `https://wa.me/${cleanPhone}` : null;
        return <motion.div key={c.id} initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: i * 0.04
        }} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-1582">
                {/* Status bar */}
                <div className={`h-1 w-full ${statusInfo.dot.replace('bg-', 'bg-')}`} data-id="element-1583" />
                <div className="p-4" data-id="element-1584">
                  <div className="flex items-start justify-between mb-3" data-id="element-1585">
                    <div className="flex items-center gap-2" data-id="element-1586">
                      <div className="w-9 h-9 rounded-full bg-purple-100 flex items-center justify-center text-[#4f1964] font-bold text-sm flex-shrink-0" data-id="element-1587">
                        {c.name.charAt(0).toUpperCase()}
                      </div>
                      <div data-id="element-1588">
                        <p className="font-semibold text-gray-900 text-sm leading-tight" data-id="element-1589">
                          {c.name}
                        </p>
                        {c.phone && <p className="text-xs text-gray-500 mt-0.5" dir="ltr" data-id="element-1590">
                            {c.phone.replace('@s.whatsapp.net', '')}
                          </p>}
                      </div>
                    </div>
                    <div className="flex gap-1" data-id="element-1591">
                      <button onClick={() => openModal('customer', c)} className="p-1.5 text-gray-400 hover:text-blue-600 rounded" data-id="element-1592">
                        <Edit className="w-3.5 h-3.5" data-id="element-1593" />
                      </button>
                      <button onClick={() => deleteItem('customer', c.id)} className="p-1.5 text-gray-400 hover:text-red-600 rounded" data-id="element-1594">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-1595" />
                      </button>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="mb-3" data-id="element-1596">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`} data-id="element-1597">
                      <span className={`w-1.5 h-1.5 rounded-full ${statusInfo.dot}`} data-id="element-1598" />
                      {isRTL ? statusInfo.labelAr : statusInfo.labelEn}
                    </span>
                  </div>

                  {/* Notes */}
                  {c.notes && <div className="flex items-start gap-1.5 bg-gray-50 rounded-lg p-2.5 mb-3" data-id="element-1599">
                      <StickyNote className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" data-id="element-1600" />
                      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed" data-id="element-1601">
                        {c.notes}
                      </p>
                    </div>}

                  {/* WhatsApp Button + Date */}
                  <div className="flex items-center justify-between mt-2" data-id="element-1602">
                    <p className="text-[10px] text-gray-400" data-id="element-1603">
                      {new Date(c.createdAt).toLocaleDateString(isRTL ? 'ar-EG' : 'en-US', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                    </p>
                    {waLink && <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-1604">
                        <Phone className="w-3 h-3" data-id="element-1605" />
                        {isRTL ? 'واتساب' : 'WhatsApp'}
                      </a>}
                  </div>
                </div>
              </motion.div>;
      })}
        </div>}

      {filteredCustomers.length > 0 && <p className="text-xs text-gray-400" data-id="element-1606">
          {isRTL ? `عرض ${filteredCustomers.length} من ${customers.length}` : `Showing ${filteredCustomers.length} of ${customers.length}`}
        </p>}
    </div>;
  // --- RENDER: PRODUCTS ---
  const renderProducts = () => <div className="space-y-6" data-id="element-1607">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-1608">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-1609">
          {isRTL ? 'المنتجات والخدمات' : 'Products & Services'}
        </h2>
        <div className="flex items-center gap-2 flex-wrap" data-id="element-1610">
          <Button size="sm" variant="outline" onClick={() => {
          const headers = isRTL ? ['اسم المنتج', 'النوع', 'السعر', 'الوصف'] : ['Product Name', 'Type', 'Price', 'Description'];
          exportToCSV(products, 'products', headers);
        }} disabled={products.length === 0} data-id="element-1611">
            <Download className="w-4 h-4 mr-1" data-id="element-1612" />
            {isRTL ? 'تصدير' : 'Export'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => productImportRef.current?.click()} data-id="element-1613">
            <Upload className="w-4 h-4 mr-1" data-id="element-1614" />
            {isRTL ? 'استيراد' : 'Import'}
          </Button>
          <input ref={productImportRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          if (file) importCSV(file, 'product');
          e.target.value = '';
        }} data-id="element-1615" />
          <Button className="bg-[#4f1964] text-white" size="sm" onClick={() => openModal('product')} data-id="element-1616">
            <Plus className="w-4 h-4 mr-1" data-id="element-1617" />
            {isRTL ? 'إضافة منتج' : 'Add Product'}
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative" data-id="element-1618">
        <Search className={`absolute top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} data-id="element-1619" />
        <input value={productSearch} onChange={e => setProductSearch(e.target.value)} placeholder={isRTL ? 'بحث بالاسم أو الوصف أو السعر...' : 'Search by name, description, or price...'} className={`w-full py-2.5 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-[#4f1964] focus:border-transparent text-sm ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'}`} data-id="element-1620" />
        {productSearch && <button onClick={() => setProductSearch('')} className={`absolute top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 ${isRTL ? 'left-3' : 'right-3'}`} data-id="element-1621">
            <X className="w-4 h-4" data-id="element-1622" />
          </button>}
      </div>

      {filteredProducts.length === 0 ? <div className="bg-white p-12 rounded-xl border text-center" data-id="element-1623">
          <ShoppingBag className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1624" />
          <p className="text-gray-500" data-id="element-1625">
            {productSearch ? isRTL ? 'لا توجد نتائج' : 'No results found' : isRTL ? 'لا توجد منتجات' : 'No products yet'}
          </p>
        </div> : <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1626">
            {filteredProducts.map(p => <div key={p.id} className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-1627">
                <div className="flex justify-between items-start mb-4" data-id="element-1628">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center" data-id="element-1629">
                    <ShoppingBag className="w-6 h-6 text-[#4f1964]" data-id="element-1630" />
                  </div>
                  <div className="flex gap-2" data-id="element-1631">
                    <button onClick={() => openModal('product', p)} className="text-gray-400 hover:text-blue-600" data-id="element-1632">
                      <Edit className="w-4 h-4" data-id="element-1633" />
                    </button>
                    <button onClick={() => deleteItem('product', p.id)} className="text-gray-400 hover:text-red-600" data-id="element-1634">
                      <Trash2 className="w-4 h-4" data-id="element-1635" />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-lg text-gray-900 mb-1" data-id="element-1636">
                  {p.name}
                </h3>
                <p className="text-gray-500 text-sm mb-4 line-clamp-2" data-id="element-1637">
                  {p.description}
                </p>
                <div className="flex justify-between items-center" data-id="element-1638">
                  <span className="font-bold text-[#4f1964]" data-id="element-1639">{p.price}</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded" data-id="element-1640">
                    {p.type}
                  </span>
                </div>
              </div>)}
          </div>
          <div className="text-xs text-gray-500" data-id="element-1641">
            {isRTL ? `${filteredProducts.length} من ${products.length} منتج` : `${filteredProducts.length} of ${products.length} products`}
          </div>
        </>}
    </div>;
  // --- RENDER: OFFERS ---
  const renderOffers = () => <div className="space-y-6" data-id="element-1642">
      <div className="flex justify-between items-center" data-id="element-1643">
        <h2 className="text-xl font-bold text-gray-900" data-id="element-1644">
          {isRTL ? 'العروض' : 'Offers'}
        </h2>
        <Button className="bg-[#4f1964] text-white" onClick={() => openModal('offer')} data-id="element-1645">
          <Plus className="w-4 h-4 mr-2" data-id="element-1646" /> {isRTL ? 'إضافة عرض' : 'Add Offer'}
        </Button>
      </div>
      {offers.length === 0 ? <div className="bg-white p-12 rounded-xl border text-center" data-id="element-1647">
          <Tag className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-1648" />
          <p className="text-gray-500" data-id="element-1649">
            {isRTL ? 'لا توجد عروض' : 'No offers yet'}
          </p>
        </div> : <div className="grid gap-4" data-id="element-1650">
          {offers.map(o => <div key={o.id} className="bg-white p-6 rounded-xl border flex justify-between items-center" data-id="element-1651">
              <div className="flex items-center gap-4" data-id="element-1652">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center" data-id="element-1653">
                  <Tag className="w-6 h-6 text-orange-600" data-id="element-1654" />
                </div>
                <div data-id="element-1655">
                  <h3 className="font-bold text-gray-900" data-id="element-1656">{o.title}</h3>
                  <p className="text-sm text-gray-500" data-id="element-1657">
                    {o.startDate} - {o.endDate}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-4" data-id="element-1658">
                <span className="text-2xl font-bold text-[#4f1964]" data-id="element-1659">
                  {o.discount}
                </span>
                <div className="flex gap-2" data-id="element-1660">
                  <button onClick={() => openModal('offer', o)} className="text-gray-400 hover:text-blue-600" data-id="element-1661">
                    <Edit className="w-4 h-4" data-id="element-1662" />
                  </button>
                  <button onClick={() => deleteItem('offer', o.id)} className="text-gray-400 hover:text-red-600" data-id="element-1663">
                    <Trash2 className="w-4 h-4" data-id="element-1664" />
                  </button>
                </div>
              </div>
            </div>)}
        </div>}
    </div>;
  // --- RENDER: INTEGRATION ---
  const renderIntegration = () => <div className="space-y-8 max-w-3xl" data-id="element-1665">
      {/* Connection Status */}
      <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-1666">
        <h3 className="font-bold text-lg text-gray-900 mb-4" data-id="element-1667">
          {isRTL ? 'حالة الاتصال' : 'Connection Status'}
        </h3>
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg" data-id="element-1668">
          <div className="flex items-center gap-3" data-id="element-1669">
            <div className={`w-3 h-3 rounded-full ${hasConnectedWhatsApp ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`} data-id="element-1670" />
            <div data-id="element-1671">
              <p className="font-medium text-gray-900" data-id="element-1672">
                {hasConnectedWhatsApp ? isRTL ? 'متصل' : 'Connected' : isRTL ? 'غير متصل' : 'Disconnected'}
              </p>
              {hasConnectedWhatsApp && user?.instanceName && <p className="text-xs text-gray-500" data-id="element-1673">{user.instanceName}</p>}
              {instanceStats?.phone && <p className="text-xs text-gray-500" data-id="element-1674">{instanceStats.phone}</p>}
            </div>
          </div>
          <Button size="sm" variant={hasConnectedWhatsApp ? 'outline' : 'primary'} onClick={() => navigate('/onboarding')} data-id="element-1675">
            {hasConnectedWhatsApp ? isRTL ? 'إعادة الربط' : 'Reconnect' : isRTL ? 'ربط الآن' : 'Connect Now'}
          </Button>
        </div>

        {/* Bot Control Buttons */}
        {hasConnectedWhatsApp && <div className="mt-6 pt-6 border-t border-gray-100" data-id="element-1676">
            <h4 className="text-sm font-medium text-gray-700 mb-3" data-id="element-1677">
              {isRTL ? 'التحكم في البوت' : 'Bot Control'}
            </h4>
            <div className="flex gap-3" data-id="element-1678">
              <button onClick={() => toggleBotState(true)} disabled={isTogglingBot || isBotEnabled} className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${isBotEnabled ? 'bg-green-100 text-green-700 cursor-default' : 'bg-white border-2 border-green-500 text-green-600 hover:bg-green-50'}`} data-id="element-1679">
                {isBotEnabled && <Check className="w-4 h-4" data-id="element-1680" />}
                {isRTL ? 'تفعيل البوت' : 'Enable Bot'}
              </button>

              <button onClick={() => toggleBotState(false)} disabled={isTogglingBot || !isBotEnabled} className={`flex-1 py-2.5 px-4 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${!isBotEnabled ? 'bg-red-100 text-red-700 cursor-default' : 'bg-white border-2 border-red-500 text-red-600 hover:bg-red-50'}`} data-id="element-1681">
                {!isBotEnabled && <Check className="w-4 h-4" data-id="element-1682" />}
                {isRTL ? 'تعطيل البوت' : 'Disable Bot'}
              </button>
            </div>

            <div className="mt-3 flex items-center gap-2 text-xs" data-id="element-1683">
              <span className="text-gray-500" data-id="element-1684">
                {isRTL ? 'الحالة الحالية:' : 'Current Status:'}
              </span>
              <span className={`font-bold ${isBotEnabled ? 'text-green-600' : 'text-red-600'}`} data-id="element-1685">
                {isBotEnabled ? isRTL ? 'يعمل الآن ✅' : 'Active Now ✅' : isRTL ? 'متوقف 🛑' : 'Stopped 🛑'}
              </span>
            </div>
          </div>}
      </div>

      {/* System Message */}
      {hasConnectedWhatsApp && <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-1686">
          <div className="flex items-center gap-2 mb-4" data-id="element-1687">
            <Bot className="w-5 h-5 text-[#4f1964]" data-id="element-1688" />
            <h3 className="font-bold text-lg text-gray-900" data-id="element-1689">
              {isRTL ? 'إعداد المساعد الذكي' : 'AI Assistant Setup'}
            </h3>
          </div>
          <p className="text-sm text-gray-600 mb-4" data-id="element-1690">
            {isRTL ? 'هذه الرسالة تحدد شخصية المساعد الذكي والمعلومات التي يجب أن يعرفها عن عملك.' : "This message defines the AI assistant's personality and information about your business."}
          </p>

          {/* Website URL Input */}
          <div className="mb-4" data-id="element-1691">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1692">
              {isRTL ? 'رابط الموقع الإلكتروني' : 'Website URL'}
            </label>
            <div className="relative" data-id="element-1693">
              <Globe className={`absolute top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} data-id="element-1694" />
              <input type="url" value={websiteUrl} onChange={e => setWebsiteUrl(e.target.value)} placeholder="https://example.com" className={`w-full py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent text-sm ${isRTL ? 'pr-9 pl-4' : 'pl-9 pr-4'}`} dir="ltr" data-id="element-1695" />
            </div>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1696">
              {isRTL ? 'سيتم إرسال هذا الرابط للبوت للتعرف على محتوى موقعك' : 'This URL will be sent to the bot to learn about your website content'}
            </p>
          </div>

          {/* PDF Upload */}
          <div className="mb-4" data-id="element-1697">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1698">
              {isRTL ? 'رفع ملف PDF (معلومات إضافية)' : 'Upload PDF (Additional Info)'}
            </label>
            <div onClick={() => pdfInputRef.current?.click()} className="border-2 border-dashed border-gray-200 rounded-xl p-4 flex flex-col items-center justify-center cursor-pointer hover:border-[#4f1964] hover:bg-purple-50 transition-colors" data-id="element-1699">
              <FileText className="w-8 h-8 text-gray-400 mb-2" data-id="element-1700" />
              <span className="text-sm text-gray-600 font-medium" data-id="element-1701">
                {pdfFile ? pdfFile.name : isRTL ? 'اضغط لرفع ملف PDF' : 'Click to upload PDF'}
              </span>
              <span className="text-xs text-gray-400 mt-1" data-id="element-1702">
                {isRTL ? 'الحد الأقصى 5 ميجابايت' : 'Max size 5MB'}
              </span>
            </div>
            <input ref={pdfInputRef} type="file" accept=".pdf" className="hidden" onChange={e => {
          const file = e.target.files?.[0];
          if (file) setPdfFile(file);
        }} data-id="element-1703" />
          </div>

          <textarea value={systemMessage} onChange={e => setSystemMessage(e.target.value)} placeholder={isRTL ? 'مثال: أنت مساعد خدمة عملاء...' : 'Example: You are a customer service assistant...'} className="w-full h-40 px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-[#4f1964] focus:outline-none resize-none text-gray-900 mb-4" dir={isRTL ? 'rtl' : 'ltr'} data-tour="system-message-input" data-id="element-1704" />
          <div className="flex justify-end" data-id="element-1705">
            <Button onClick={handleSaveSystemMessage} isLoading={isSavingMessage} disabled={!systemMessage.trim()} size="sm" data-id="element-1706">
              <Save className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-1707" />
              {isRTL ? 'حفظ الإعدادات' : 'Save Settings'}
            </Button>
          </div>
        </div>}

      {/* Finish & Activate */}
      {hasConnectedWhatsApp && hasSystemMessage && <div data-tour="finish-button" data-id="element-1708">
          <Button onClick={handleFinish} variant="primary" fullWidth size="lg" isLoading={isFinishing} className="shadow-lg bg-gradient-to-r from-[#4f1964] to-purple-600 hover:from-[#3d1350] hover:to-purple-700 relative overflow-hidden group" data-id="element-1709">
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300" data-id="element-1710" />
            <Check className={`w-5 h-5 ${isRTL ? 'ml-2' : 'mr-2'}`} data-id="element-1711" />
            {isRTL ? 'إنهاء وتفعيل المساعد' : 'Finish & Activate Assistant'}
          </Button>

          {/* Pulse effect if tour step 3 */}
          {isTourOpen && tourStepIndex === 2 && <div className="text-center mt-2 text-sm text-[#4f1964] font-bold animate-bounce" data-id="element-1712">
              {isRTL ? '👆 اضغط هنا للتفعيل!' : '👆 Click here to activate!'}
            </div>}
        </div>}

      {/* Available Integrations */}
      <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-1713">
        <h3 className="font-bold text-lg text-gray-900 mb-4" data-id="element-1714">
          {isRTL ? 'التكاملات' : 'Integrations'}
        </h3>
        <div className="grid grid-cols-1 gap-4" data-id="element-1715">
          {INTEGRATIONS.map(integration => <div key={integration.id} className="p-5 rounded-xl border border-gray-200 bg-white" data-id="element-1716">
              <div className="flex items-center justify-between" data-id="element-1717">
                <div className="flex items-center gap-4" data-id="element-1718">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${integration.color} flex items-center justify-center shadow-sm`} data-id="element-1719">
                    <integration.icon className="w-6 h-6 text-white" data-id="element-1720" />
                  </div>
                  <div data-id="element-1721">
                    <div className="flex items-center gap-2" data-id="element-1722">
                      <p className="font-semibold text-gray-900" data-id="element-1723">
                        {isRTL ? integration.nameAr : integration.name}
                      </p>
                      {integration.isBeta && <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 border border-amber-200 rounded-full" data-id="element-1724">
                          {isRTL ? 'تجريبي' : 'Beta'}
                        </span>}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5" data-id="element-1725">
                      {isRTL ? integration.descriptionAr : integration.description}
                    </p>
                  </div>
                </div>
                <span className="text-xs text-blue-600 bg-blue-50 px-3 py-1 rounded-full font-medium" data-id="element-1726">
                  {isRTL ? 'قريباً' : 'Coming Soon'}
                </span>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
  // --- RENDER: CONVERSATIONS ---
  const renderConversations = () => <ChatPage instanceName={user?.instanceName || ''} isRTL={isRTL} language={language as 'ar' | 'en'} hasConnectedWhatsApp={hasConnectedWhatsApp} isBotEnabled={isBotEnabled} subscriptionActive={user?.plan !== 'trial' || trialDaysLeft !== null && trialDaysLeft > 0} onNavigateToIntegration={() => setActiveSection('integration')} onToggleBot={() => toggleBotState(!isBotEnabled)} onNavigateToSubscription={() => navigate('/subscription')} data-id="element-1727" />;
  // --- RENDER: SETTINGS ---
  const renderSettings = () => <div className="max-w-4xl mx-auto space-y-6" data-id="element-1728">
      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-1729">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1730">
          {isRTL ? 'إعدادات الحساب' : 'Account Settings'}
        </h2>
        <div className="flex items-center gap-6 mb-6" data-id="element-1731">
          <div className="relative group cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-1732">
            {profileImage ? <img src={profileImage} alt="Profile" className="w-20 h-20 rounded-full object-cover border-4 border-purple-100" data-id="element-1733" /> : <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#4f1964] to-purple-500 flex items-center justify-center text-white text-2xl font-bold border-4 border-purple-100" data-id="element-1734">
                {user?.name?.charAt(0) || 'U'}
              </div>}
            <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1735">
              <Camera className="w-6 h-6 text-white" data-id="element-1736" />
            </div>
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleProfileImageUpload} data-id="element-1737" />
          </div>
          <div data-id="element-1738">
            <p className="font-bold text-lg text-gray-900" data-id="element-1739">{user?.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1740">{user?.email}</p>
            <p className="text-xs text-purple-600 mt-1" data-id="element-1741">
              {isRTL ? `خطة: ${userPlan}` : `Plan: ${userPlan}`}
            </p>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4" data-id="element-1742">
          <div data-id="element-1743">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1744">
              {isRTL ? 'الاسم' : 'Name'}
            </label>
            <input value={editName} onChange={e => setEditName(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent" data-id="element-1745" />
          </div>
          <div data-id="element-1746">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1747">
              {isRTL ? 'البريد الإلكتروني' : 'Email'}
            </label>
            <input value={editEmail} onChange={e => setEditEmail(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent" data-id="element-1748" />
          </div>
          <div data-id="element-1749">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1750">
              {isRTL ? 'رقم الهاتف' : 'Phone Number'}
            </label>
            <input value={editPhone} onChange={e => setEditPhone(e.target.value)} dir="ltr" placeholder="+20 1xxxxxxxxx" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent" data-id="element-1751" />
          </div>
          <div data-id="element-1752">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1753">
              {isRTL ? 'اسم الشركة / البراند' : 'Company / Brand Name'}
            </label>
            <input value={editBrandName} onChange={e => setEditBrandName(e.target.value)} placeholder={isRTL ? 'يُستخدم كلوجو في لوحة التحكم' : 'Used as logo in dashboard'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent" data-id="element-1754" />
          </div>
        </div>
        <div className="mb-4" data-id="element-1755">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1756">
            {isRTL ? 'عنوان الشركة' : 'Company Address'}
          </label>
          <input value={editCompanyAddress} onChange={e => setEditCompanyAddress(e.target.value)} placeholder={isRTL ? 'مثال: القاهرة، مصر' : 'e.g. Cairo, Egypt'} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] focus:border-transparent" data-id="element-1757" />
        </div>
        <Button size="sm" onClick={handleSaveProfile} isLoading={isSavingProfile} className="bg-[#4f1964] text-white" data-id="element-1758">
          {isSavingProfile ? <Loader2 className="w-5 h-5 animate-spin mx-auto" data-id="element-1759" /> : isRTL ? 'حفظ التغييرات' : 'Save Changes'}
        </Button>
      </div>

      <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm" data-id="element-1760">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-1761">
          {isRTL ? 'تغيير كلمة المرور' : 'Change Password'}
        </h2>
        <div className="space-y-4 max-w-md" data-id="element-1762">
          <div data-id="element-1763">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1764">
              {isRTL ? 'كلمة المرور الحالية' : 'Current Password'}
            </label>
            <input type="password" value={oldPassword} onChange={e => setOldPassword(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" dir="ltr" data-id="element-1765" />
          </div>
          <div data-id="element-1766">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1767">
              {isRTL ? 'كلمة المرور الجديدة' : 'New Password'}
            </label>
            <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" dir="ltr" data-id="element-1768" />
          </div>
          <div data-id="element-1769">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1770">
              {isRTL ? 'تأكيد كلمة المرور' : 'Confirm Password'}
            </label>
            <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" dir="ltr" data-id="element-1771" />
          </div>
          <Button onClick={handleResetPassword} className="w-full bg-[#4f1964] text-white hover:bg-[#3d1350]" data-id="element-1772">
            {isRTL ? 'تحديث كلمة المرور' : 'Update Password'}
          </Button>
        </div>
      </div>

      {/* Subscription */}
      <div className="bg-white p-6 rounded-xl border border-gray-200" data-id="element-1773">
        <h3 className="font-bold text-lg text-gray-900 mb-4 flex items-center gap-2" data-id="element-1774">
          <Crown className="w-5 h-5 text-yellow-500" data-id="element-1775" />
          {isRTL ? 'الاشتراك' : 'Subscription'}
        </h3>
        <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg mb-4" data-id="element-1776">
          <div className="flex items-center justify-between mb-3" data-id="element-1777">
            <div className="flex items-center gap-2" data-id="element-1778">
              <span className={`px-3 py-1 rounded-full text-sm font-bold ${userPlan === 'trial' ? 'bg-yellow-100 text-yellow-700' : userPlan === 'pro' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`} data-id="element-1779">
                {userPlan.toUpperCase()}
              </span>
              <span className="text-gray-600 text-sm" data-id="element-1780">
                {isRTL ? 'الخطة الحالية' : 'Current Plan'}
              </span>
            </div>
          </div>
          {userPlan === 'trial' && trialDaysLeft !== null && <div data-id="element-1781">
              <div className="flex justify-between text-sm mb-1" data-id="element-1782">
                <span className="text-gray-600" data-id="element-1783">
                  {isRTL ? 'أيام التجربة' : 'Trial Days'}
                </span>
                <span className={`font-bold ${trialDaysLeft <= 2 ? 'text-red-600' : trialDaysLeft <= 4 ? 'text-yellow-600' : 'text-green-600'}`} data-id="element-1784">
                  {trialDaysLeft} / 7
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-1785">
                <div className="h-2 rounded-full bg-gradient-to-r from-[#4f1964] to-purple-500 transition-all" style={{
              width: `${Math.max(0, (7 - trialDaysLeft) / 7 * 100)}%`
            }} data-id="element-1786" />
              </div>
              {user?.trialEndDate && <p className="text-xs text-gray-500 mt-2" data-id="element-1787">
                  {isRTL ? 'ينتهي في:' : 'Ends:'}{' '}
                  {new Date(user.trialEndDate).toLocaleDateString()}
                </p>}
            </div>}
        </div>
        <Button onClick={() => navigate('/subscription')} className="bg-[#4f1964] text-white" data-id="element-1788">
          <CreditCard className="w-4 h-4 mr-2" data-id="element-1789" />
          {isRTL ? 'ترقية الخطة' : 'Upgrade Plan'}
        </Button>
      </div>
    </div>;
  // --- RENDER: SUPPORT ---
  const renderSupport = () => <SupportTickets userEmail={user?.email || ''} userName={user?.name || ''} userPhone={user?.phone || ''} instanceName={user?.instanceName || ''} language={language} isRTL={isRTL} data-id="element-1790" />;
  // --- MODAL ---
  const renderModal = () => {
    if (!isModalOpen) return null;
    return <AnimatePresence data-id="element-1791">
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setIsModalOpen(false)} data-id="element-1792">
          <motion.div initial={{
          scale: 0.95,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} exit={{
          scale: 0.95,
          opacity: 0
        }} className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-1793">
            <h3 className="text-xl font-bold mb-4" data-id="element-1794">
              {editingItem ? isRTL ? 'تعديل' : 'Edit' : isRTL ? 'إضافة' : 'Add'}
            </h3>
            <form onSubmit={e => {
            e.preventDefault();
            const fd = new FormData(e.currentTarget);
            const data: any = Object.fromEntries(fd.entries());
            if (editingItem) data.id = editingItem.id;
            saveItem(modalType!, data);
          }} className="space-y-4" data-id="element-1795">
              {modalType === 'product' && <>
                  <input name="name" defaultValue={editingItem?.name} placeholder={isRTL ? 'اسم المنتج' : 'Product Name'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" required data-id="element-1796" />
                  <select name="type" defaultValue={editingItem?.type || 'product'} className="w-full p-3 border border-gray-300 rounded-lg" data-id="element-1797">
                    <option value="product" data-id="element-1798">
                      {isRTL ? 'منتج' : 'Product'}
                    </option>
                    <option value="service" data-id="element-1799">
                      {isRTL ? 'خدمة' : 'Service'}
                    </option>
                  </select>
                  <input name="price" defaultValue={editingItem?.price} placeholder={isRTL ? 'السعر' : 'Price'} className="w-full p-3 border border-gray-300 rounded-lg" required data-id="element-1800" />
                  <textarea name="description" defaultValue={editingItem?.description} placeholder={isRTL ? 'الوصف' : 'Description'} className="w-full p-3 border border-gray-300 rounded-lg" data-id="element-1801" />
                  <input name="link" defaultValue={editingItem?.link} placeholder={isRTL ? 'رابط المنتج (اختياري)' : 'Product Link (optional)'} className="w-full p-3 border border-gray-300 rounded-lg" type="url" dir="ltr" data-id="element-1802" />
                </>}
              {modalType === 'offer' && <>
                  <input name="title" defaultValue={editingItem?.title} placeholder={isRTL ? 'عنوان العرض' : 'Offer Title'} className="w-full p-3 border border-gray-300 rounded-lg" required data-id="element-1803" />
                  <input name="discount" defaultValue={editingItem?.discount} placeholder={isRTL ? 'الخصم' : 'Discount'} className="w-full p-3 border border-gray-300 rounded-lg" required data-id="element-1804" />
                  <div className="grid grid-cols-2 gap-2" data-id="element-1805">
                    <input type="date" name="startDate" defaultValue={editingItem?.startDate} className="w-full p-3 border border-gray-300 rounded-lg" required data-id="element-1806" />
                    <input type="date" name="endDate" defaultValue={editingItem?.endDate} className="w-full p-3 border border-gray-300 rounded-lg" required data-id="element-1807" />
                  </div>
                  <input name="link" defaultValue={editingItem?.link} placeholder={isRTL ? 'رابط العرض (اختياري)' : 'Offer Link (optional)'} className="w-full p-3 border border-gray-300 rounded-lg" type="url" dir="ltr" data-id="element-1808" />
                </>}
              {modalType === 'customer' && <>
                  <div data-id="element-1809">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1810">
                      {isRTL ? 'اسم العميل *' : 'Customer Name *'}
                    </label>
                    <input name="name" defaultValue={editingItem?.name} placeholder={isRTL ? 'أدخل اسم العميل' : 'Enter customer name'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" required data-id="element-1811" />
                  </div>
                  <div data-id="element-1812">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1813">
                      {isRTL ? 'رقم الهاتف' : 'Phone Number'}
                    </label>
                    <input name="phone" defaultValue={editingItem?.phone} placeholder="+20 1xxxxxxxxx" className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" dir="ltr" data-id="element-1814" />
                  </div>
                  <div data-id="element-1815">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1816">
                      {isRTL ? 'الحالة' : 'Status'}
                    </label>
                    <select name="status" defaultValue={editingItem?.status || 'new_lead'} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964]" data-id="element-1817">
                      <option value="new_lead" data-id="element-1818">
                        {isRTL ? 'جديد' : 'New Lead'}
                      </option>
                      <option value="contacted" data-id="element-1819">
                        {isRTL ? 'تم التواصل' : 'Contacted'}
                      </option>
                      <option value="waiting_response" data-id="element-1820">
                        {isRTL ? 'في انتظار الرد' : 'Waiting Response'}
                      </option>
                      <option value="qualified" data-id="element-1821">
                        {isRTL ? 'مؤهل' : 'Qualified'}
                      </option>
                      <option value="appointment_scheduled" data-id="element-1822">
                        {isRTL ? 'موعد محدد' : 'Appointment Scheduled'}
                      </option>
                      <option value="won" data-id="element-1823">{isRTL ? 'تم الفوز' : 'Won'}</option>
                      <option value="cancelled" data-id="element-1824">
                        {isRTL ? 'ملغي' : 'Cancelled'}
                      </option>
                    </select>
                  </div>
                  <div data-id="element-1825">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1826">
                      {isRTL ? 'الملاحظات / ملخص المحادثة' : 'Notes / Conversation Summary'}
                    </label>
                    <textarea name="notes" defaultValue={editingItem?.notes} placeholder={isRTL ? 'أضف ملاحظات أو ملخص المحادثة...' : 'Add notes or conversation summary...'} rows={4} className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4f1964] resize-none" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-1827" />
                  </div>
                </>}
              <div className="flex justify-end gap-2 pt-2" data-id="element-1828">
                <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} data-id="element-1829">
                  {isRTL ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button type="submit" className="bg-[#4f1964] text-white" data-id="element-1830">
                  {isRTL ? 'حفظ' : 'Save'}
                </Button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      </AnimatePresence>;
  };
  // Suspension check
  if (user?.isAccountSuspended) {
    navigate('/account-suspended');
    return null;
  }
  const sidebarItems = [{
    id: 'dashboard',
    label: isRTL ? 'لوحة التحكم' : 'Dashboard',
    icon: LayoutDashboard,
    tourId: 'sidebar-dashboard'
  }, {
    id: 'customers',
    label: isRTL ? 'المتابعة' : 'Follow-up',
    icon: UserCheck,
    tourId: 'sidebar-customers'
  }, {
    id: 'products',
    label: isRTL ? 'المنتجات والخدمات' : 'Products & Services',
    icon: Package,
    tourId: 'sidebar-products'
  }, {
    id: 'offers',
    label: isRTL ? 'العروض' : 'Offers',
    icon: Tag,
    tourId: 'sidebar-offers'
  }, {
    id: 'conversations',
    label: isRTL ? 'المحادثات' : 'Conversations',
    icon: MessagesSquare,
    tourId: 'sidebar-conversations'
  }, {
    id: 'integration',
    label: isRTL ? 'التكاملات' : 'Integration',
    icon: Zap,
    tourId: 'sidebar-integration'
  }, {
    id: 'settings',
    label: isRTL ? 'الإعدادات' : 'Settings',
    icon: Settings,
    tourId: 'sidebar-settings'
  }, {
    id: 'support',
    label: isRTL ? 'الدعم الفني' : 'Get Support',
    icon: MessageSquare,
    tourId: 'sidebar-support'
  }];
  return <div className="min-h-screen bg-gray-50 flex" dir={isRTL ? 'rtl' : 'ltr'} data-id="element-1831">
      <AnimatePresence data-id="element-1832">
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setMobileMenuOpen(false)} className="fixed inset-0 bg-black/50 z-40 md:hidden" data-id="element-1833" />}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside className={`fixed md:static inset-y-0 ${isRTL ? 'right-0' : 'left-0'} z-50 w-64 bg-[#4f1964] text-white transform transition-transform duration-300 ${mobileMenuOpen ? 'translate-x-0' : isRTL ? 'translate-x-full' : '-translate-x-full'} md:translate-x-0 flex flex-col`} data-id="element-1834">
        <div className="p-5 border-b border-purple-800 flex items-center justify-between" data-id="element-1835">
          <div className="flex items-center gap-3" data-id="element-1836">
            {profileImage ? <img src={profileImage} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-purple-300" data-id="element-1837" /> : <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center text-white font-bold text-sm border-2 border-purple-300" data-id="element-1838">
                {brandName.charAt(0)}
              </div>}
            <motion.span className="font-bold text-lg truncate max-w-[140px]" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} key={brandName} data-id="element-1839">
              {brandName}
            </motion.span>
          </div>
          <button onClick={() => setMobileMenuOpen(false)} className="md:hidden text-white" data-id="element-1840">
            <X className="w-6 h-6" data-id="element-1841" />
          </button>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" data-id="element-1842">
          {sidebarItems.map(item => <button key={item.id} onClick={() => {
          setActiveSection(item.id as Section);
          setMobileMenuOpen(false);
        }} data-tour={item.tourId} className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${activeSection === item.id ? 'bg-white/15 text-white font-medium' : 'text-purple-200 hover:bg-white/5 hover:text-white'}`} data-id="element-1843">
              <item.icon className="w-5 h-5" data-id="element-1844" />
              <span className="flex-1 text-start" data-id="element-1845">{item.label}</span>
              {item.id === 'support' && supportUnreadCount > 0 && <span className="w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center" data-id="element-1846">
                  {supportUnreadCount}
                </span>}
            </button>)}
        </nav>
      </motion.aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col min-w-0 overflow-hidden ${activeSection === 'conversations' ? 'p-2 sm:p-3' : 'p-4 sm:p-6 lg:p-8'}`} data-id="element-1847">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 h-16 flex items-center justify-between px-4 sm:px-6 lg:px-8" data-id="element-1848">
          <div className="flex items-center gap-4" data-id="element-1849">
            <button onClick={() => setMobileMenuOpen(true)} className="md:hidden text-gray-500" data-id="element-1850">
              <Menu className="w-6 h-6" data-id="element-1851" />
            </button>
            <h1 className="text-xl font-bold text-gray-900" data-id="element-1852">
              {sidebarItems.find(i => i.id === activeSection)?.label}
            </h1>
          </div>
          <div className="flex items-center gap-3" data-id="element-1853">
            {/* Bot Status Indicator (Replaced Toggle) */}
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${isBotEnabled ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-id="element-1854">
              <div className={`w-2 h-2 rounded-full ${isBotEnabled ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`} data-id="element-1855" />
              {isBotEnabled ? isRTL ? 'البوت يعمل' : 'Bot Active' : isRTL ? 'البوت متوقف' : 'Bot Stopped'}
            </div>

            {/* Notifications */}
            <div className="relative" data-dropdown data-id="element-1856">
              <button onClick={() => {
              setIsNotifOpen(!isNotifOpen);
              setIsLangOpen(false);
              setIsProfileOpen(false);
            }} className="relative p-2 text-gray-500 hover:text-[#4f1964] rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1857">
                <Bell className="w-5 h-5" data-id="element-1858" />
                {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-id="element-1859">
                    {unreadCount}
                  </span>}
              </button>
              <AnimatePresence data-id="element-1860">
                {isNotifOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-full mt-2 bg-white border rounded-xl shadow-xl w-80 z-50 overflow-hidden ${isRTL ? 'left-0' : 'right-0'}`} data-id="element-1861">
                    <div className="p-3 border-b flex justify-between items-center" data-id="element-1862">
                      <span className="font-bold text-gray-900" data-id="element-1863">
                        {isRTL ? 'الإشعارات' : 'Notifications'}
                      </span>
                      {unreadCount > 0 && <button onClick={markAllAsRead} className="text-xs text-[#4f1964] hover:underline" data-id="element-1864">
                          {isRTL ? 'قراءة الكل' : 'Mark all read'}
                        </button>}
                    </div>
                    <div className="max-h-72 overflow-y-auto" data-id="element-1865">
                      {notifications.length === 0 ? <p className="p-4 text-center text-gray-500 text-sm" data-id="element-1866">
                          {isRTL ? 'لا توجد إشعارات' : 'No notifications'}
                        </p> : notifications.slice(0, 10).map(n => <div key={n.id} className={`p-3 border-b last:border-0 ${!n.read ? 'bg-purple-50/50' : ''}`} data-id="element-1867">
                            <div className="flex items-start gap-2" data-id="element-1868">
                              <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${n.type === 'warning' ? 'bg-yellow-500' : n.type === 'success' ? 'bg-green-500' : 'bg-blue-500'}`} data-id="element-1869" />
                              <div data-id="element-1870">
                                <p className="text-sm font-medium text-gray-900" data-id="element-1871">
                                  {isRTL ? n.titleAr : n.title}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5" data-id="element-1872">
                                  {isRTL ? n.messageAr : n.message}
                                </p>
                              </div>
                            </div>
                          </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Language */}
            <div className="relative" data-dropdown data-id="element-1873">
              <button onClick={() => {
              setIsLangOpen(!isLangOpen);
              setIsNotifOpen(false);
              setIsProfileOpen(false);
            }} className="p-2 text-gray-500 hover:text-[#4f1964] rounded-lg hover:bg-gray-100 transition-colors" data-id="element-1874">
                <Globe className="w-5 h-5" data-id="element-1875" />
              </button>
              <AnimatePresence data-id="element-1876">
                {isLangOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-full mt-2 bg-white border rounded-lg shadow-lg py-1 w-32 z-50 ${isRTL ? 'left-0' : 'right-0'}`} data-id="element-1877">
                    <button onClick={() => {
                  setLanguage('ar');
                  setIsLangOpen(false);
                }} className={`w-full px-4 py-2 text-start hover:bg-gray-50 text-sm ${language === 'ar' ? 'text-[#4f1964] font-medium' : ''}`} data-id="element-1878">
                      🇸🇦 العربية
                    </button>
                    <button onClick={() => {
                  setLanguage('en');
                  setIsLangOpen(false);
                }} className={`w-full px-4 py-2 text-start hover:bg-gray-50 text-sm ${language === 'en' ? 'text-[#4f1964] font-medium' : ''}`} data-id="element-1879">
                      🇺🇸 English
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>

            {/* Profile */}
            <div className="relative" data-dropdown data-id="element-1880">
              <button onClick={() => {
              setIsProfileOpen(!isProfileOpen);
              setIsLangOpen(false);
              setIsNotifOpen(false);
            }} className="flex items-center gap-2" data-id="element-1881">
                {profileImage ? <img src={profileImage} alt="" className="w-8 h-8 rounded-full object-cover border-2 border-gray-200" data-id="element-1882" /> : <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-[#4f1964] font-bold text-sm" data-id="element-1883">
                    {user?.name?.charAt(0) || 'U'}
                  </div>}
              </button>
              <AnimatePresence data-id="element-1884">
                {isProfileOpen && <motion.div initial={{
                opacity: 0,
                y: -10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} className={`absolute top-full mt-2 bg-white border rounded-lg shadow-lg py-2 w-48 z-50 ${isRTL ? 'left-0' : 'right-0'}`} data-id="element-1885">
                    <div className="px-4 py-2 border-b border-gray-100" data-id="element-1886">
                      <p className="font-medium text-gray-900 text-sm" data-id="element-1887">
                        {user?.name}
                      </p>
                      <p className="text-xs text-gray-500 truncate" data-id="element-1888">
                        {user?.email}
                      </p>
                    </div>
                    <button onClick={() => {
                  setActiveSection('settings');
                  setIsProfileOpen(false);
                }} className="w-full px-4 py-2 text-start text-gray-700 hover:bg-gray-50 flex items-center gap-2 text-sm" data-id="element-1889">
                      <Settings className="w-4 h-4" data-id="element-1890" />
                      {isRTL ? 'الإعدادات' : 'Settings'}
                    </button>
                    <button onClick={() => {
                  logout();
                  navigate('/login');
                }} className="w-full px-4 py-2 text-start text-red-600 hover:bg-red-50 flex items-center gap-2 text-sm" data-id="element-1891">
                      <LogOut className="w-4 h-4" data-id="element-1892" />
                      {isRTL ? 'تسجيل الخروج' : 'Logout'}
                    </button>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className={`flex-1 overflow-y-auto ${activeSection === 'conversations' ? 'p-2 sm:p-3' : 'p-4 sm:p-6 lg:p-8'}`} data-id="element-1893">
          <AnimatePresence mode="wait" data-id="element-1894">
            <motion.div key={activeSection} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} transition={{
            duration: 0.2
          }} className={activeSection === 'conversations' ? 'h-full' : ''} data-id="element-1895">
              {activeSection === 'dashboard' && renderDashboard()}
              {activeSection === 'customers' && renderCustomers()}
              {activeSection === 'products' && renderProducts()}
              {activeSection === 'offers' && renderOffers()}
              {activeSection === 'conversations' && renderConversations()}
              {activeSection === 'integration' && renderIntegration()}
              {activeSection === 'settings' && renderSettings()}
              {activeSection === 'support' && renderSupport()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
      {renderModal()}
      <ChatWidget language={language as 'ar' | 'en'} data-id="element-1896" />

      {/* Guided Tour */}
      <GuidedTour steps={tourSteps} isOpen={isTourOpen} onClose={() => setIsTourOpen(false)} onComplete={handleTourComplete} isRTL={isRTL} currentStepIndex={tourStepIndex} onStepChange={handleTourStepChange} data-id="element-1897" />
    </div>;
}